import { useState } from 'react';
import { Button, Dropdown, type MenuProps } from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  KeyOutlined,
  MoreOutlined,
  StopOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import type { UserResponse } from '../../../api/types';
import { useT } from '../../../locales';
import { CreateKeycloakUserModal } from './CreateKeycloakUserModal';
import { ResetUserPasswordAction } from './ResetUserPasswordAction';

interface UsersTableActionsProps {
  user: UserResponse;
  canUpdate: boolean;
  canReadRoles: boolean;
  canDelete: boolean;
  onDetail: (userId: string) => void;
  onEdit: (user: UserResponse) => void;
  onManageRoles: (userId: string) => void;
  onDelete: (user: UserResponse) => void;
}

export function UsersTableActions({
  user,
  canUpdate,
  canReadRoles,
  canDelete,
  onDetail,
  onEdit,
  onManageRoles,
  onDelete,
}: UsersTableActionsProps) {
  const t = useT();
  const [resetOpen, setResetOpen] = useState(false);
  const [createKeycloakOpen, setCreateKeycloakOpen] = useState(false);

  const menuItems: MenuProps['items'] = [
    {
      key: 'detail',
      label: t('common.ver'),
      icon: <EyeOutlined aria-hidden />,
    },
    ...(canUpdate
      ? [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined aria-hidden />,
          },
          ...(canReadRoles
            ? [
                {
                  key: 'roles',
                  label: t('pages.users.assignRoles'),
                  icon: <UsergroupAddOutlined aria-hidden />,
                },
              ]
            : []),
          ...(user.keycloakId
            ? [
                {
                  key: 'reset',
                  label: t('pages.users.resetPassword'),
                  icon: <KeyOutlined aria-hidden />,
                },
              ]
            : [
                {
                  key: 'create-keycloak',
                  label: t('pages.users.createKeycloakAction'),
                  icon: <UserAddOutlined aria-hidden />,
                },
              ]),
        ]
      : []),
    ...(canDelete
      ? [
          {
            key: 'delete',
            label: t('pages.users.deactivate'),
            icon: <StopOutlined aria-hidden />,
            danger: true,
          },
        ]
      : []),
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'detail':
        onDetail(user.id);
        break;
      case 'edit':
        onEdit(user);
        break;
      case 'roles':
        onManageRoles(user.id);
        break;
      case 'reset':
        setResetOpen(true);
        break;
      case 'create-keycloak':
        setCreateKeycloakOpen(true);
        break;
      case 'delete':
        onDelete(user);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Dropdown trigger={['click']} menu={{ items: menuItems, onClick: handleMenuClick }}>
        <Button
          type="text"
          size="small"
          icon={<MoreOutlined aria-hidden />}
          aria-label={t('common.actions')}
        />
      </Dropdown>
      {canUpdate && user.keycloakId && (
        <ResetUserPasswordAction
          userId={user.id}
          hiddenTrigger
          open={resetOpen}
          onOpenChange={setResetOpen}
        />
      )}
      <CreateKeycloakUserModal
        user={user}
        open={createKeycloakOpen}
        onClose={() => setCreateKeycloakOpen(false)}
      />
    </>
  );
}
